import { ChessTermType, TermAction, } from "@chesshotel/types";
import { distance, exchangeEvaluation, getFen, key2pos, uci2move, } from "./util.js";
import { Chess, KING, KNIGHT, QUEEN, swapColor, } from "@chesshotel/chess";
import { isValidFenMove } from "./tactics/util.js";
export function positiveKingSafety(position, lastPosition) {
    const castle = detectCastle(lastPosition.fen, position.move);
    if (castle) {
        return { ...castle, termAction: TermAction.PERFORM };
    }
    const kingSafety = detectPositiveKingSafety(position, lastPosition);
    if (kingSafety)
        return kingSafety;
}
export function positivePreventCastling(position, lastPosition) {
    const castle = detectPreventCastling(lastPosition.fen, position.move);
    if (castle) {
        return {
            ...castle,
            termAction: TermAction.PERFORM,
        };
    }
}
export function negativePreventCastling(position, lastPosition) {
    const user = detectPreventCastling(lastPosition.fen, position.move);
    const best = detectPreventCastling(lastPosition.fen, lastPosition.bestMove);
    if (!user && best) {
        const arrows = [{ from: best.move.from, to: best.move.to }];
        return {
            ...best,
            arrows,
            termAction: TermAction.MISS,
        };
    }
    const opponentMove = position.pvMoves[0];
    const opponent = detectPreventCastling(position.fen, opponentMove);
    if (opponent) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        const bestOpponent = isValid && detectPreventCastling(bestFen, opponentMove);
        if (!bestOpponent) {
            const arrows = [{ from: opponent.move.from, to: opponent.move.to }];
            return { ...opponent, arrows, termAction: TermAction.ALLOW };
        }
    }
}
export function negativeKingSafety(position, lastPosition) {
    const kingSafety = detectNegativeKingSafety(position, lastPosition);
    if (kingSafety)
        return kingSafety;
    const castle = detectNegativeCastle(position, lastPosition);
    if (castle)
        return castle;
}
function detectNegativeCastle(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const user = detectCastle(lastPosition.fen, position.move);
    const best = detectCastle(lastPosition.fen, lastPosition.bestMove);
    if (!user && best) {
        return {
            ...best,
            termAction: TermAction.MISS,
        };
    }
}
function detectNegativeKingSafety(position, lastPosition) {
    const threats = lastPosition.threats;
    if (!threats) {
        return;
    }
    const user = detectWorseKingSafety(lastPosition.fen, position.move);
    const best = detectWorseKingSafety(lastPosition.fen, lastPosition.bestMove);
    if (user && !best) {
        return {
            ...user,
            termAction: TermAction.PERFORM,
        };
    }
}
function detectPositiveKingSafety(position, lastPosition) {
    const user = detectBetterKingSafety(lastPosition.fen, position.move);
    if (user) {
        return {
            ...user,
            termAction: TermAction.PERFORM,
        };
    }
}
export function isCastleMove(fen, moveStr) {
    const chess = new Chess(fen);
    const move = chess.move(moveStr);
    return (move.piece == KING && distance(key2pos(move.from), key2pos(move.to)) >= 2);
}
function detectCastle(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const move = chess.move(moveStr);
    const isCastle = move.piece == KING && distance(key2pos(move.from), key2pos(move.to)) >= 2;
    if (isCastle) {
        const mainPiece = chess.getBoardPiece(move.to);
        const translationKeys = {
            color,
        };
        return {
            term: ChessTermType.CASTLE,
            translationKeys,
            move,
            mainPiece,
        };
    }
}
function detectCanOpponentCastle(fen, moveStr) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(moveStr));
    const color = chess.turn();
    const king = chess.getKing(color);
    const moves = chess.moves({ verbose: true, square: king });
    const canCastle = moves.find((move) => move.piece == KING && distance(key2pos(move.from), key2pos(move.to)) > 1);
    if (canCastle) {
        const mainPiece = chess.getBoardPiece(moveResult.to);
        return {
            term: ChessTermType.CASTLE,
            mainPiece,
        };
    }
}
function canCastle(fen, color) {
    const chess = new Chess(fen);
    if (chess.turn() != color) {
        chess.changeTurn();
    }
    const king = chess.getKing(color);
    const moves = chess.moves({ verbose: true, square: king, legal: false });
    const canCastle = moves.find((move) => move.piece == KING && distance(key2pos(move.from), key2pos(move.to)) > 1);
    return !!canCastle;
}
function detectBetterKingSafety(fen, moveStr) {
    const kingSafety = detectKingSafety(fen, moveStr);
    if (kingSafety?.isBetter) {
        return kingSafety.chessTerm;
    }
}
function detectWorseKingSafety(fen, moveStr) {
    const kingSafety = detectKingSafety(fen, moveStr);
    if (kingSafety?.isWorse) {
        return kingSafety.chessTerm;
    }
}
function detectKingSafety(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const kingSquare = chess.getKing(color);
    const squaresBefore = chess.getCheckingSquares(kingSquare);
    const threatsBefore = getKingThreats(fen, color, squaresBefore);
    const moveObj = uci2move(moveStr);
    const moveResult = chess.move(moveObj);
    if (chess.inCheck()) {
        return;
    }
    const squaresAfter = chess.getCheckingSquares(kingSquare);
    const threatsAfter = getKingThreats(chess.fen(), color, squaresAfter);
    const isThreatsWorse = threatsAfter > threatsBefore;
    const isThreatsBetter = threatsAfter < threatsBefore;
    const isWorse = isThreatsWorse;
    const isBetter = isThreatsBetter;
    if (isWorse || isBetter) {
        return {
            isWorse,
            isBetter,
            chessTerm: {
                term: isWorse
                    ? ChessTermType.BAD_KING_SAFETY
                    : ChessTermType.KING_SAFETY,
                mainPiece: chess.getBoardPiece(moveObj.to),
                move: moveResult,
            },
        };
    }
}
function getKingThreats(fen, color, checkSquares) {
    const chess = new Chess(fen);
    const opponentColor = swapColor(color);
    const opponentPieces = chess.pieces(opponentColor);
    if (chess.turn() != opponentColor) {
        chess.changeTurn();
    }
    let threatCount = 0;
    for (const piece of opponentPieces) {
        if (piece.type == KING) {
            continue;
        }
        let pieceMoves = chess.uciMoves(piece).map((move) => uci2move(move));
        if (piece.type != KNIGHT) {
            pieceMoves = pieceMoves.filter((move) => checkSquares.includes(move.to));
        }
        pieceMoves.forEach((move) => {
            chess.move(move);
            if (chess.inCheck()) {
                threatCount++;
            }
            chess.undo();
        });
    }
    return threatCount;
}
function detectPreventCastling(fen, move) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const castlingRights = chess.getCastlingRights(opponentColor);
    if (castlingRights[KING] == false && castlingRights[QUEEN] == false) {
        return null;
    }
    const castleBefore = canCastle(fen, opponentColor);
    const moveResult = chess.move(uci2move(move));
    if (chess.inCheck()) {
        return;
    }
    const evalResult = exchangeEvaluation(chess.fen(), moveResult.to, {
        backtrackBadMoves: true,
    });
    if (evalResult.gain > 0) {
        return;
    }
    const castleAfter = canCastle(chess.fen(), opponentColor);
    if (castleBefore && !castleAfter) {
        return {
            term: ChessTermType.PREVENT_CASTLING,
            move: moveResult,
        };
    }
}
//# sourceMappingURL=kingSafety.js.map